import React, { useState, useEffect } from 'react';
import { Stack, Drawer } from '@mui/material';
import EnhancedItemInfo from './EnhancedItemInfo';
import ItemInformation from './ItemInformation';
import Optimization from '../CardItems/Optimizations/Optimization';
import { theme } from '../../../theme';
import EditItemPanel from './EditItemPanel';
import EditItemDrawer from './EditItemDrawer';
import PeerItemDetail from './PeerItemDetails';
import AddItemPanel from './AddItemPanel';
import { useLogUserActionMutation } from '../../../api/physicianDashboardSlice';
import DrawerHeader from '../../CustomComponents/DrawerHeader';

// There will always be an item, even if it is an addition rec, there will always be a draft which will be physician's own draft.
// The peerDraft may or may not be present depending on the variation/source of the page.
function ItemDetails({ open, close, item, draft, peerDraft = null, variant = 'card_review' }) {
    return (
        <Drawer open={open} onClose={close} anchor='right' PaperProps={{ sx: { width: { xs: '100%', sm: '75%', md: '50%' }, maxWidth: '600px' } }}>
            { item && draft &&
                <ItemDetailsBody 
                    open={open} 
                    close={close} 
                    item={item} 
                    draft={draft} 
                    peerDraft={peerDraft} 
                    variant={variant} 
                />
            }
        </Drawer>
    );
}

export default ItemDetails;

function ItemDetailsBody({ open, close, item, draft, peerDraft, variant }) {
    const [isEditing, setIsEditing] = useState(false);
    const [logUserAction] = useLogUserActionMutation();

    // The next few lines are a bit complex to ensure we get the correct item from the physician's draft if it exists on there or in a recommendation
    let physicianItem = null;
    const itemsArr = draft.items[item.category] ?? [];
    const recsArr = draft.recommendations[item.category] ?? [];

    if (itemsArr.length > 0) {
        physicianItem = itemsArr.find((i) => i.id === item.id) || null;
    }
    if (!physicianItem && recsArr.length > 0) {
        // Helper functions to clarify complex conditions to find the physicianItem by matching the item id to either the item or the recommended item.
        const isCustomAdditionOrAddition = (rec) =>
            rec.item &&
            rec.item.id === item.id &&
            (rec.status === 'custom_addition' || rec.type === 'addition');
        const isCustomSwapOrCompliance = (rec) =>
            rec.recommended_item &&
            rec.recommended_item.id === item.id &&
            (rec.status === 'custom_swap' || rec.type === 'swap' || rec.type === 'compliance');
        const rec = recsArr.find(
            (rec) => isCustomAdditionOrAddition(rec) || isCustomSwapOrCompliance(rec)
        );
        physicianItem = rec ? rec.item : null;
    }

    const peerItem = peerDraft && peerDraft.items[item.category] ?
        peerDraft.items[item.category].find((i) => i.id === item.id) :
        null;

    // Will catch addition, custom_addition, swap, compliance, custom_swap etc since I pass the item in the props and check against recommended_id
    const recommendation = draft.recommendations[item.category] ?
        draft.recommendations[item.category].find((rec) => (rec.item.id === item.id || rec.recommended_item?.id === item.id)) :
        null;

    const isSwap = recommendation?.type === 'swap' || recommendation?.type === 'compliance';

    useEffect(() => {
        if (open) {
            logUserAction({
                action: 'opened_item_details',
                data: {
                    item_id: item.id,
                    draft_id: draft.id,
                    physician_item: physicianItem,
                    peer_item: peerItem,
                    recommendation,
                    variant,
                },
            });
        }
    }, [open]);

    return (
        <>
            <DrawerHeader handleBack={close} title="More Details" />
            <Stack backgroundColor={theme.palette.background.categoryDrawer} spacing={2} p={2} height='100%'>
                <ItemInformation item={item} />

                {/* If there is a rec, it implies this is on the physician's draft
                since we only check their own draft for recs, hence, we want to show the optimization */}
                {/* However, we do not want to show swap recs as optimizations since they have a separate view */}
                { recommendation && !isSwap &&
                    <Optimization 
                        recommendation={recommendation} 
                        propItem={physicianItem} 
                        draft={draft} 
                        hideDetailsButton
                    />
                }

                {/* My Card */}   
                {/* Needs changes in conditional rendering */}
                { physicianItem && (
                    <EditItemPanel
                        physicianItem={physicianItem} 
                        peerItem={peerItem}
                        recommendation={recommendation} 
                        draft={draft} 
                        setIsEditing={setIsEditing} 
                        closeItemDetails={close} 
                        variant={variant}
                    />
                )}
                { !physicianItem && variant==='peer_comparison' && (
                    <AddItemPanel 
                        setIsEditing={setIsEditing}
                    />
                )}

                {/* Peer Card */}
                { peerDraft && 
                    <PeerItemDetail
                        physician_name={`${peerDraft.card.doctor.first_name} ${peerDraft.card.doctor.last_name}`}
                        peerItem={peerItem}
                    />
                }

                <EnhancedItemInfo item={item} hospitalId={draft.card.hospital_id} />

                <EditItemDrawer
                    open={isEditing} 
                    setOpen={setIsEditing} 
                    closeItemInfo={close}
                    item={item} 
                    draft={draft} 
                    recommendation={recommendation} 
                />
            </Stack>
        </>
    );
}